import React, { useState, useEffect } from 'react';
import { patientConsultationsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  FileText,
  Calendar,
  User,
  Stethoscope,
  Download,
  Eye,
  Loader2,
  ClipboardList,
  Pill,
  CalendarCheck,
  Image,
  FileDown,
  ExternalLink,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const NEXT_APPOINTMENT_LABELS = {
  '1_week': 'Dans 1 semaine',
  '1_month': 'Dans 1 mois',
  '3_months': 'Dans 3 mois',
  '6_months': 'Dans 6 mois',
  'not_needed': 'Non nécessaire'
};

const PatientConsultations = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    loadConsultations();
  }, []);

  const loadConsultations = async () => {
    try {
      const response = await patientConsultationsAPI.getAll();
      setConsultations(response.data.consultations || []);
    } catch (error) {
      console.error('Error loading consultations:', error);
      toast.error('Erreur lors du chargement des consultations');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async (consultation) => {
    setExporting(consultation.id);
    try {
      const response = await patientConsultationsAPI.exportPDF(consultation.id);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DOCKHTA_Consultation_${consultation.date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setExporting(null);
    }
  };

  const openDetail = (consultation) => {
    setSelectedConsultation(consultation);
    setShowDetailModal(true);
  };

  const getAppointmentTypeLabel = (type) => {
    switch (type) {
      case 'video_call': return 'Téléconsultation';
      case 'home_visit': return 'Domicile';
      default: return 'Consultation';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#E53935] animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6" data-testid="patient-consultations-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Mes consultations
        </h1>
        <p className="text-slate-500 mt-1">
          Historique et récapitulatifs de vos consultations
        </p>
      </div>

      {/* Consultations List */}
      {consultations.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="text-center py-12">
            <ClipboardList className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">Aucune consultation</h3>
            <p className="text-slate-500 mt-2">
              Vos récapitulatifs de consultation apparaîtront ici après vos rendez-vous.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {consultations.map((consultation) => (
            <Card 
              key={consultation.id} 
              className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer"
              onClick={() => openDetail(consultation)}
              data-testid={`consultation-${consultation.id}`}
            >
              <CardContent className="p-5">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Main Info */}
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1976D2] to-[#1A237E] flex items-center justify-center flex-shrink-0">
                      <Stethoscope className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900">Dr. {consultation.professional_name}</h3>
                        <Badge className="bg-blue-100 text-blue-800 text-xs">
                          {consultation.specialty}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {consultation.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {getAppointmentTypeLabel(consultation.appointment_type)}
                        </span>
                        {consultation.documents && consultation.documents.length > 0 && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                            <Image className="w-3 h-3 mr-1" />
                            {consultation.documents.length} document(s)
                          </Badge>
                        )}
                      </div>
                      {/* Preview of diagnosis */}
                      <p className="text-sm text-slate-600 mt-2 line-clamp-2">
                        <strong>Diagnostic:</strong> {consultation.diagnosis}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-auto" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openDetail(consultation)}
                      className="border-[#1976D2] text-[#1976D2]"
                      data-testid={`view-${consultation.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Voir
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleExportPDF(consultation)}
                      disabled={exporting === consultation.id}
                      className="bg-[#E53935] hover:bg-[#C62828]"
                      data-testid={`export-${consultation.id}`}
                    >
                      {exporting === consultation.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <FileDown className="w-4 h-4 mr-1" />
                          PDF
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#1976D2]" />
              Récapitulatif de consultation
            </DialogTitle>
          </DialogHeader>
          
          {selectedConsultation && (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="bg-gradient-to-r from-[#1976D2]/10 to-[#1A237E]/10 rounded-xl p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#1976D2] flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Dr. {selectedConsultation.professional_name}</h3>
                    <p className="text-sm text-slate-500">{selectedConsultation.specialty}</p>
                    <p className="text-sm text-[#1976D2] font-medium mt-1">
                      {selectedConsultation.date} • {getAppointmentTypeLabel(selectedConsultation.appointment_type)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Diagnosis */}
              <div>
                <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
                  <Stethoscope className="w-4 h-4 text-[#E53935]" />
                  Diagnostic / Observations
                </h4>
                <p className="text-slate-600 bg-slate-50 rounded-lg p-4 whitespace-pre-wrap">
                  {selectedConsultation.diagnosis}
                </p>
              </div>

              {/* Recommendations */}
              {selectedConsultation.recommendations && (
                <div>
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
                    <ClipboardList className="w-4 h-4 text-blue-600" />
                    Recommandations
                  </h4>
                  <p className="text-slate-600 bg-blue-50 rounded-lg p-4 whitespace-pre-wrap">
                    {selectedConsultation.recommendations}
                  </p>
                </div>
              )}

              {/* Treatment */}
              {selectedConsultation.treatment && (
                <div>
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
                    <Pill className="w-4 h-4 text-emerald-600" />
                    Traitement prescrit
                  </h4>
                  <p className="text-slate-600 bg-emerald-50 rounded-lg p-4 whitespace-pre-wrap">
                    {selectedConsultation.treatment}
                  </p>
                </div>
              )}

              {/* Next Appointment */}
              {selectedConsultation.next_appointment && selectedConsultation.next_appointment !== 'not_needed' && (
                <div className="flex items-center gap-3 bg-amber-50 rounded-lg p-4">
                  <CalendarCheck className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="text-sm text-amber-800 font-medium">Prochain rendez-vous conseillé</p>
                    <p className="text-amber-700">{NEXT_APPOINTMENT_LABELS[selectedConsultation.next_appointment]}</p>
                  </div>
                </div>
              )}

              {/* Documents */}
              {selectedConsultation.documents && selectedConsultation.documents.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
                    <Image className="w-4 h-4 text-purple-600" />
                    Documents & Ordonnances ({selectedConsultation.documents.length})
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedConsultation.documents.map((doc) => (
                      <a
                        key={doc.id}
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors group"
                      >
                        <div className="w-10 h-10 rounded-lg bg-purple-200 flex items-center justify-center">
                          {doc.file_type === 'pdf' ? (
                            <FileText className="w-5 h-5 text-purple-700" />
                          ) : (
                            <Image className="w-5 h-5 text-purple-700" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-purple-900 truncate">{doc.title}</p>
                          <p className="text-xs text-purple-600">{doc.file_type === 'pdf' ? 'PDF' : 'Image'}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-purple-400 group-hover:text-purple-600" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowDetailModal(false)}
                  className="flex-1"
                >
                  Fermer
                </Button>
                <Button
                  onClick={() => handleExportPDF(selectedConsultation)}
                  disabled={exporting === selectedConsultation.id}
                  className="flex-1 bg-[#E53935] hover:bg-[#C62828]"
                >
                  {exporting === selectedConsultation.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Download className="w-5 h-5 mr-2" />
                      Télécharger en PDF
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientConsultations;
